import { EditorContextService } from "@magnolia/frontend-helpers-base";
import { environment } from "./environment";
import { wfetch } from "./utils/fetch";
import { getApiEndpoint } from "./utils/constants/api";
import { getAvailableLanguages, stripLanguageFromPathname } from "./utils/languages";
import { getUrlQueryParameters, extractQueryParameters, extractQueryParameter } from "./utils/url";
import { getFullPath } from "./utils/location";
import { deriveAppBase, setCurrentAppBase } from "./utils/appBase";

/**
 * Decides which Magnolia workspace a browser path belongs to, and with it which endpoints to
 * read and which node path prefix applies.
 *
 * The app used to assume one source: everything under APP_BASE in `website`. Component
 * Factory items break that assumption because they are pages in their own workspace, which
 * is why they could be edited but not previewed headless. Resolution is by path prefix
 * rather than by a flag, so the same PageLoader serves both and a project that installs no
 * Component Factory never leaves the default branch.
 *
 * @param {string} pathname - Browser path, language prefix already stripped.
 * @returns {{rootNodePath: string, workspace: string, pagesEndpoint: string, annotationsEndpoint: string}}
 */
export function resolveContentSource(pathname) {
    const factoryBase = environment.COMPONENT_FACTORY_BASE;

    if (factoryBase && (pathname === factoryBase || pathname.startsWith(factoryBase + "/"))) {
        return {
            rootNodePath: factoryBase,
            workspace: environment.COMPONENT_FACTORY_WORKSPACE,
            pagesEndpoint: "COMPONENT_FACTORY_PAGES",
            annotationsEndpoint: "COMPONENT_FACTORY_ANNOTATIONS",
        };
    }

    // Website content: the base is derived from the path, so every /shops/<name> tree is its
    // own site with its own root page; everything else keeps the legacy APP_BASE (/home).
    return {
        rootNodePath: deriveAppBase(pathname),
        workspace: "website",
        pagesEndpoint: "PAGES",
        annotationsEndpoint: "TEMPLATE_ANNOTATIONS",
    };
}

/**
 * Fetches site info for one node, bypassing the redux store.
 *
 * The store holds the site of the storefront root, resolved once at startup. That is the
 * wrong site for a factory item: Magnolia resolves an item to the site whose definition maps
 * its folder, so the item's languages are the target site's, not the storefront's. Asking per
 * node is what makes the language switch in the preview match the real page.
 *
 * @param {string} nodePath - The node path.
 * @param {string} workspace - The workspace the node lives in.
 * @returns {Promise<object|null>} Site info JSON or null.
 */
async function fetchSiteInfo(nodePath, workspace) {
    const response = await wfetch(getApiEndpoint("SITE_INFO", { pathParams: { workspace, path: nodePath } }));

    return response ? response.data : null;
}

export function getMagnoliaContext(location, availableLanguages = [], rootNodePath = environment.APP_BASE) {
    const fullPath = getFullPath(location);

    return EditorContextService.getMagnoliaContext(fullPath, rootNodePath, availableLanguages);
}

/**
 * Gets the node path from the URL, removing the language prefix.
 * @param {string[]} languages - Available language codes.
 * @param {string} rootNodePath - The root node path (e.g., '/home').
 * @returns {string} The node path without language prefix.
 */
function getNodePath(currentLanguageCode, fullPath, rootNodePath) {
    let pathname = fullPath;

    // Remove language prefix if present
    pathname = stripLanguageFromPathname(pathname, currentLanguageCode);

    // If pathname is just '/', return the rootNodePath
    if (pathname === "/") {
        return rootNodePath;
    }

    // If pathname doesn't start with rootNodePath, prepend it
    if (!pathname.startsWith(rootNodePath)) {
        return rootNodePath + pathname;
    }

    return pathname;
}

/**
 * Fetches the page content from Magnolia's delivery API.
 * @param {string} nodePath - The node path (without language prefix).
 * @param {string} currentLanguageCode - The current language code.
 * @param {string} endpoint - Key in API_ENDPOINTS; differs per workspace.
 * @returns {Promise<object|null>} The page content JSON or null on error.
 */
export async function fetchPageContent(nodePath, currentLanguageCode, endpoint = "PAGES") {
    const urlParams = getUrlQueryParameters();
    const mgnlVersion = extractQueryParameter(urlParams, "mgnlVersion");
    const previewParams = extractQueryParameters(urlParams, ["mgnlPreview", "mgnlChannel"]);
    const queryParams = {
        lang: currentLanguageCode,
        ...(mgnlVersion && { version: mgnlVersion }),
        ...previewParams,
    };

    const response = await wfetch(getApiEndpoint(endpoint, { pathParams: { path: nodePath }, queryParams }));

    if (!response) return null;

    return response.data;
}

/**
 * Fetches the template annotations from Magnolia for the Page Editor.
 * @param {string} nodePath - The node path (without language prefix).
 * @param {boolean} isMagnolia - Whether we're in Magnolia editor context.
 * @param {string} endpoint - Key in API_ENDPOINTS; differs per workspace.
 * @returns {Promise<object>} The template annotations JSON or an empty object.
 */
async function fetchTemplateAnnotations(nodePath, isMagnolia, endpoint = "TEMPLATE_ANNOTATIONS") {
    if (!isMagnolia) return {};

    const urlParams = getUrlQueryParameters();
    const queryParams = extractQueryParameters(urlParams, ["mgnlVersion", "mgnlPreview", "mgnlChannel", "mgnlLocale"]);

    const response = await wfetch(getApiEndpoint(endpoint, { pathParams: { path: nodePath }, queryParams }));

    if (!response) return {};

    return response.data;
}

/**
 * Main function to get all necessary data for a Magnolia page.
 * @param {string} fullPath - The current window location (pathname + search).
 * @param {object} siteData - The site data object.
 * @param {string} currentLanguageCode - The current language code.
 * @returns {Promise<object>} An object containing page, navigationContent, templateAnnotations, and magnoliaContext.
 */
export async function getMagnoliaProps(location, siteData, currentLanguageCode) {
    // Which workspace this path belongs to has to be decided before anything else, because
    // the node path prefix, both endpoints and the site all follow from it. The language
    // prefix is stripped first so that /de/<factory root>/... is recognised too.
    const source = resolveContentSource(stripLanguageFromPathname(location.pathname, currentLanguageCode));

    // Keep the module-level base current for consumers without router access (link
    // composition, static page links) before this page's components render.
    if (source.workspace === "website") setCurrentAppBase(source.rootNodePath);

    // Node path is built from pathname only — including location.search here
    // would leak query params (e.g. ?category=/foo) into the delivery URL,
    // producing a malformed `pages/...?category=...?lang=en` double-`?` URL.
    const nodePath = getNodePath(currentLanguageCode, location.pathname, source.rootNodePath);

    // The store's site is the storefront's. For anything outside it, ask for the site of this
    // node instead, otherwise the language switch offers the storefront's locales on a page
    // that belongs to a different site.
    const site = source.workspace === "website" ? siteData : await fetchSiteInfo(nodePath, source.workspace);
    const availableLanguages = getAvailableLanguages(site);

    // Get magnolia context from the library (used for EditablePage component)
    const magnoliaContext = getMagnoliaContext(location, availableLanguages, source.rootNodePath);

    const content = await fetchPageContent(nodePath, currentLanguageCode, source.pagesEndpoint);
    const templateAnnotations = await fetchTemplateAnnotations(nodePath, magnoliaContext.isMagnolia, source.annotationsEndpoint);

    return {
        content,
        templateAnnotations,
        magnoliaContext,
    };
}
